// Client-side error reports. Each error a member's browser meets — a React
// crash (components/ErrorBoundary.tsx), an uncaught error, an unhandled
// rejection, a response that drifted from its expected shape (lib/validate.ts)
// — is posted to /api/client-error, which writes it to CloudWatch as one line
// (routes/client-error.js on the server). Until 2026-10-20 these went to the
// Application Insights JS SDK, configured from /api/config; its Azure
// subscription was disabled in July 2026, the connection string went empty,
// and every browser error since went nowhere.
//
// Each message is reported once and a page reports at most ten, so a render
// loop that throws on every frame costs one line, not thousands. Reporting
// never throws and never waits on the answer; a report that fails is dropped.

const PATH = '/api/client-error';
const MAX_REPORTS = 10;
/** A browser driven by automation says so (navigator.webdriver, as CaliberShell
 *  reads it): the mocked browser suite, whose specs list the exact requests a
 *  page makes and whose mocks provoke errors on purpose. It reports nothing.
 *  People never see this flag set. */
const AUTOMATED = typeof navigator !== 'undefined' && navigator.webdriver === true;
/** Messages browsers raise that say nothing about Caliber's code: a cross-origin
 *  script's hidden error, and ResizeObserver's benign loop notice. */
const IGNORED = /^(Script error\.?|ResizeObserver loop .*)$/;

let installed = false;
let sent = 0;
const seen = new Set<string>();

/** Report the errors nothing else catches. Call once, before the first render. */
export function initTelemetry(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (event) => {
    trackException(event.error ?? event.message, { source: 'window-error' });
  });
  window.addEventListener('unhandledrejection', (event) => {
    trackException(event.reason, { source: 'unhandled-rejection' });
  });
}

/** Up to six frames of a stack, one string each, the line repeating `message` dropped. */
function framesOf(stack: string | undefined, message?: string): string[] {
  return String(stack ?? '').split('\n').map((line) => line.trim())
    .filter((line) => line && !(message && line.endsWith(message))).slice(0, 6);
}

/** Report one error. `source` says where it was caught; `componentStack`
 *  (the error boundary's) and `label` (lib/validate.ts's response) ride along. */
export function trackException(error: unknown, properties: Record<string, unknown> = {}): void {
  if (AUTOMATED) return;
  try {
    const err = error instanceof Error ? error : new Error(typeof error === 'string' ? error : String(error));
    if (!err.message || IGNORED.test(err.message)) return;
    const key = `${err.name}: ${err.message}`;
    if (sent >= MAX_REPORTS || seen.has(key)) return;
    seen.add(key);
    sent += 1;
    const componentStack = typeof properties.componentStack === 'string' ? properties.componentStack : undefined;
    const body = JSON.stringify({
      source: properties.source,
      name: err.name,
      message: err.message,
      stack: framesOf(err.stack, err.message),
      components: componentStack ? framesOf(componentStack) : undefined,
      label: properties.label,
      path: window.location.pathname,
    });
    void fetch(PATH, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body })
      .catch(() => { /* a report that fails is dropped */ });
  } catch { /* reporting must never break the app */ }
}
